import React from "react";
import { useNavigate } from "react-router-dom";
import { useEventContext } from "../context/EventContext";

const EventCard: React.FC<{
  id: string;
  title: string;
  date: string;
  time: string;
  address: string;
  onClick: () => void;
}> = ({ title, date, time, address, onClick }) => {
  const formattedDate = new Date(date);
  const month = formattedDate
    .toLocaleString("default", { month: "short" })
    .toUpperCase();
  const day = formattedDate.getDate();

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-md overflow-hidden flex cursor-pointer transform transition-transform duration-200 hover:scale-105"
    >
      <div className="w-24 bg-primary text-white flex flex-col items-center justify-center p-4">
        <span className="text-sm font-bold">{month}</span>
        <span className="text-3xl font-bold">{day}</span>
      </div>

      <div className="flex-1 p-4">
        <div className="relative">
          <div className="bg-secondary bg-opacity-10 rounded-lg p-4">
            <h3 className="text-lg font-bold text-primary mb-2">{title}</h3>
            <div className="space-y-1">
              <div className="flex items-center text-sm text-gray-600">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
                {new Date(date).toLocaleDateString(undefined, {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
                {time}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
                {address}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Events: React.FC = () => {
  const { activeEvents, loading, error, initialized, refreshEvents } =
    useEventContext();
  const navigate = useNavigate();
  const currentTime = new Date().getTime();

  // Filter upcoming events (events happening within the next 12 hours or later)
  const upcomingEvents = activeEvents.filter((event) => {
    const eventDate = new Date(event.date).getTime();
    return eventDate > currentTime - 12 * 60 * 60 * 1000;
  });

  // Filter past events (events that happened more than 12 hours ago)
  const pastEvents = activeEvents.filter((event) => {
    const eventDate = new Date(event.date).getTime();
    return eventDate <= currentTime - 12 * 60 * 60 * 1000;
  });

  const handleEventClick = (eventId: string) => {
    navigate(`/event/${eventId}`);
  };

  // Show loading state
  if (!initialized || loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
      </div>
    );
  }

  // Show error state with retry button
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-red-600">{error}</p>
        <button
          onClick={() => refreshEvents()}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && (
        <div>
          <h1 className="text-3xl font-bold text-primary mb-8">
            Upcoming Events
          </h1>
          <div className="grid gap-6">
            {upcomingEvents.map((event) => (
              <EventCard
                key={event.id}
                id={event.id}
                title={event.title}
                date={event.date}
                time={event.time}
                address={event.address}
                onClick={() => handleEventClick(event.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Past Events Section */}
      {pastEvents.length > 0 && (
        <div className={upcomingEvents.length > 0 ? "mt-12" : ""}>
          <h1 className="text-3xl font-bold text-primary mb-8">Past Events</h1>
          <div className="grid gap-6">
            {pastEvents.map((event) => (
              <EventCard
                key={event.id}
                id={event.id}
                title={event.title}
                date={event.date}
                time={event.time}
                address={event.address}
                onClick={() => handleEventClick(event.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* No Events State */}
      {activeEvents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-xl text-gray-600">
            No upcoming events at this time.
          </p>
          <p className="text-gray-500 mt-2">Check back soon for new events!</p>
        </div>
      )}
    </div>
  );
};

export default Events;
